const QUADRANT_HEADER = /^\s*quadrantChart\b/m;
const AXIS_LINE = /^(\s*)([xy]-axis)\s+(.*)$/;
const QUADRANT_LABEL_LINE = /^(\s*)(quadrant-[1-4])\s+(.*)$/;
// `Name[:::class]: [x, y][ radius: 12, color: #fff]` — the name is matched lazily
// so a name that itself contains a colon ("Store: North") still parses.
const POINT_LINE = /^(\s*)(.+?)(:::[\w-]+)?\s*:\s*\[([^\]]+)\](.*)$/;

// Keep rescaled points off the chart border so their labels aren't clipped.
const SCALE_MIN = 0.05;
const SCALE_MAX = 0.95;

/** @param {string} text */
function quote(text) {
  const inner = text.trim().replace(/^"(.*)"$/, '$1').replace(/"/g, "'");
  return inner ? `"${inner}"` : '';
}

/** @param {string} raw */
function parseNumber(raw) {
  const value = Number(raw.replace(/[$%,\s]/g, ''));
  return raw.trim() && Number.isFinite(value) ? value : undefined;
}

// `[$120,000, 45%]` — prefer splitting on a comma followed by whitespace so
// thousands separators inside a value survive; fall back to a bare comma.
/** @param {string} raw */
function parseCoordinates(raw) {
  let parts = raw.split(/,\s+/);
  if (parts.length !== 2) parts = raw.split(',');
  if (parts.length !== 2) return undefined;

  const [x, y] = parts.map(parseNumber);
  return x === undefined || y === undefined ? undefined : { x, y };
}

/** @param {number[]} values */
function getScaler(values) {
  if (values.every((value) => value >= 0 && value <= 1)) return (/** @type {number} */ v) => v;

  const min = Math.min(...values);
  const max = Math.max(...values);
  if (min === max) return () => 0.5;
  return (/** @type {number} */ v) => SCALE_MIN + ((v - min) / (max - min)) * (SCALE_MAX - SCALE_MIN);
}

/** @param {number} value */
function formatCoordinate(value) {
  const rounded = Math.round(value * 1000) / 1000;
  if (rounded >= 1) return '1';
  if (rounded <= 0) return '0';
  return String(rounded);
}

/**
 * @param {string} chart
 * @returns {string}
 */
export function normalizeQuadrantChart(chart) {
  if (!QUADRANT_HEADER.test(chart)) return chart;

  const lines = chart.split('\n');

  /** @type {{ index: number, indent: string, name: string, className: string, x: number, y: number, rest: string }[]} */
  const points = [];

  const normalized = lines.map((line, index) => {
    const trimmed = line.trim();
    if (!trimmed || trimmed.startsWith('%%') || /^(quadrantChart|title|classDef)\b/.test(trimmed)) {
      return line;
    }

    const axis = line.match(AXIS_LINE);
    if (axis) {
      const [, indent, keyword, text] = axis;
      const labels = text.split('-->').map(quote);
      return `${indent}${keyword} ${labels.filter(Boolean).join(' --> ')}`;
    }

    const quadrant = line.match(QUADRANT_LABEL_LINE);
    if (quadrant) {
      const [, indent, keyword, text] = quadrant;
      return `${indent}${keyword} ${quote(text)}`;
    }

    const point = line.match(POINT_LINE);
    if (point) {
      const [, indent, name, className = '', rawCoordinates, rest] = point;
      const coordinates = parseCoordinates(rawCoordinates);
      if (coordinates) {
        points.push({ index, indent, name, className, ...coordinates, rest });
      }
    }

    return line;
  });

  if (points.length) {
    const scaleX = getScaler(points.map((p) => p.x));
    const scaleY = getScaler(points.map((p) => p.y));

    points.forEach(({ index, indent, name, className, x, y, rest }) => {
      normalized[index] =
        `${indent}${quote(name)}${className}: [${formatCoordinate(scaleX(x))}, ${formatCoordinate(scaleY(y))}]${rest}`;
    });
  }

  return normalized.join('\n');
}
